import React from "react";
import { Plus, Trash2 } from "lucide-react";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { RefSelect } from "@/components/forms/RefSelect";
import { Combobox } from "@/components/forms/Combobox";
const NOTES_MAX = 280;
const LED_BY_MAX = 120;
const LED_BY_SUGGESTIONS = ["Self / Founders", "Friends & Family", "Undisclosed"];

const blank = () => ({
  round_type: "seed", custom_round_type: "", amount_raised: "", target_amount: "",
  currency: "USD", as_of_date: "", led_by: "", status: "closed", notes: "",
});

/** Repeatable funding rounds/events on the Business Profile financial card. */
export function FundingHistoryEditor({ entries = [], onChange, references }) {
  const currencyOptions = (references.currencies || []).map((c) => ({ value: c.value, label: c.label, pinned: c.pinned }));
  const patch = (i, key, value) => onChange(entries.map((e, idx) => (idx === i ? { ...e, [key]: value } : e)));

  return (
    <div className="space-y-4" data-testid="funding-history-editor">
      {entries.length === 0 && (
        <p className="text-sm text-muted-foreground">No funding entries yet. Add one to build your funding history — it becomes the source for Total raised.</p>
      )}
      {entries.map((e, i) => (
        <div key={e.id || i} className="rounded-xl border border-border p-4" data-testid={`funding-entry-${i}`}>
          <div className="grid grid-cols-1 gap-4 sm:grid-cols-2">
            <div className="space-y-1.5"><Label>Round type</Label>
              <RefSelect list={references.funding_round_types} value={e.round_type} onChange={(v) => patch(i, "round_type", v)} placeholder="Select" testId={`funding-round-type-${i}`} />
            </div>
            <div className="space-y-1.5"><Label>Status</Label>
              <RefSelect list={references.funding_statuses} value={e.status} onChange={(v) => patch(i, "status", v)} placeholder="Select" testId={`funding-status-${i}`} />
            </div>
            {e.round_type === "other" && (
              <div className="space-y-1.5 sm:col-span-2"><Label>Name this funding type *</Label>
                <Input maxLength={60} value={e.custom_round_type || ""} onChange={(ev) => patch(i, "custom_round_type", ev.target.value)} placeholder="e.g. Revenue-based financing" data-testid={`funding-custom-type-${i}`} />
              </div>
            )}
            <div className="space-y-1.5"><Label>Amount raised</Label>
              <Input type="number" min="0" step="any" value={e.amount_raised ?? ""} onChange={(ev) => patch(i, "amount_raised", ev.target.value)} placeholder="0" data-testid={`funding-amount-${i}`} />
            </div>
            <div className="space-y-1.5"><Label>Target amount <span className="text-muted-foreground">(optional)</span></Label>
              <Input type="number" min="0" step="any" value={e.target_amount ?? ""} onChange={(ev) => patch(i, "target_amount", ev.target.value)} placeholder="Leave blank if none" data-testid={`funding-target-${i}`} />
            </div>
            <div className="space-y-1.5"><Label>Currency</Label>
              <Combobox options={currencyOptions} value={e.currency || "USD"} onChange={(v) => patch(i, "currency", v)} placeholder="USD — US Dollar" searchPlaceholder="Search currencies…" testId={`funding-currency-${i}`} />
            </div>
            <div className="space-y-1.5"><Label>{e.status === "closed" ? "Closed date" : "As of date"}</Label>
              <Input type="date" value={e.as_of_date || ""} onChange={(ev) => patch(i, "as_of_date", ev.target.value)} data-testid={`funding-date-${i}`} />
            </div>
            <div className="space-y-1.5 sm:col-span-2"><Label>Led by</Label>
              <Input maxLength={LED_BY_MAX} value={e.led_by || ""} onChange={(ev) => patch(i, "led_by", ev.target.value)} placeholder="Foundry Group, Self / Founders, Undisclosed…" data-testid={`funding-led-by-${i}`} />
              <div className="flex flex-wrap gap-1.5 pt-1">
                {LED_BY_SUGGESTIONS.map((s) => (
                  <button key={s} type="button" onClick={() => patch(i, "led_by", s)}
                    className="rounded-full border border-border px-2.5 py-0.5 text-xs text-muted-foreground transition-colors hover:bg-secondary"
                    data-testid={`funding-led-by-quick-${i}-${s.split(" ")[0].toLowerCase()}`}>{s}</button>
                ))}
              </div>
            </div>
            <div className="space-y-1.5 sm:col-span-2"><Label>Notes <span className="text-muted-foreground">(optional)</span></Label>
              <Input maxLength={NOTES_MAX} value={e.notes || ""} onChange={(ev) => patch(i, "notes", ev.target.value)} placeholder="Extension round, oversubscribed, non-dilutive grant…" data-testid={`funding-notes-${i}`} />
            </div>
          </div>
          <div className="mt-3 flex justify-end">
            <Button type="button" variant="ghost" size="sm" className="text-destructive"
              onClick={() => onChange(entries.filter((_, idx) => idx !== i))} data-testid={`funding-remove-${i}`}>
              <Trash2 className="mr-1.5 h-4 w-4" /> Remove
            </Button>
          </div>
        </div>
      ))}
      <Button type="button" variant="outline" onClick={() => onChange([...entries, blank()])} data-testid="funding-add-entry">
        <Plus className="mr-1.5 h-4 w-4" /> Add funding entry
      </Button>
    </div>
  );
}
